import { Checkbox, DialogContent, DialogContentText, FormControl, FormControlLabel, Stack } from "@mui/material";
import { useEffect, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { BindDialogReturn, DialogProps, DialogState, bindDialog, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";

export type TwoStepConfirmationDialogInitValues = {
    title: JSX.Element | string | null | undefined;
    explanation: JSX.Element | string | null | undefined;
    confirmationText: JSX.Element | string | null | undefined;
};

export type TwoStepConfirmationDialogProps = DialogProps<TwoStepConfirmationDialogInitValues>;

export default function TwoStepConfirmationDialog({
    open,
    onClose,
    values
}: TwoStepConfirmationDialogProps) {

    const { t } = useTranslation();
    const [confirmed, setConfirmed] = useState<boolean>(false);

    // clear the checkbox each time the dialog is opened
    useEffect(() => {
        if (open) {
            setConfirmed(false);
        }
    }, [open]);

    async function onOkay() {

        onClose && onClose({
            reason: "ok"
        });
    }

    async function onCancel() {
        onClose && onClose({
            reason: "cancel"
        });
    }


    function onChange(event: React.ChangeEvent<HTMLInputElement>) {
        setConfirmed(event.target.checked);
    };

    return (
        <>
            <ResponsiveDialog
                title={values?.title ?? ""}
                enableOkay={confirmed}
                okayLabel={t("buttons.confirm")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? onOkay()
                            : onCancel();
                    }
                }
            >
                <DialogContent>
                    <Stack direction="column" spacing={2}>
                        <DialogContentText component="div">
                            {values?.explanation}
                        </DialogContentText>

                        <DialogContentText component="div">
                            <FormControl>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={confirmed}
                                            onChange={onChange}
                                        />
                                    }
                                    label={values?.confirmationText}
                                    labelPlacement="end"
                                />
                            </FormControl>
                        </DialogContentText>
                    </Stack>
                </DialogContent>
            </ResponsiveDialog>
        </>
    );
}

export type TwoStepConfirmationDialogState = DialogState<TwoStepConfirmationDialogInitValues>;
export type BindTwoStepConfirmationDialogReturn = BindDialogReturn<TwoStepConfirmationDialogInitValues>;

export function useTwoStepConfirmationDialogState(): TwoStepConfirmationDialogState {
    return useDialogState();
}

export function bindTwoStepConfirmationDialog(props: TwoStepConfirmationDialogState): BindTwoStepConfirmationDialogReturn {
    return bindDialog(props);
}
